
import React, { useContext, useEffect, useState } from 'react';
import EmployeeSelector from "../../components-user/news/EmployeeSelector";
import { MDBInput, MDBCheckbox, MDBBtn, MDBRow, MDBCol, MDBTypography, MDBInputGroup, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter, MDBChip, MDBCard, MDBCardBody, MDBCardTitle, MDBFile, MDBSpinner, MDBIcon, MDBTextArea, MDBSelect, MDBSwitch } from 'mdb-react-ui-kit';
import ReactCountryFlag from "react-country-flag";
import { useTranslation } from "react-i18next";
import { Editor } from 'react-draft-wysiwyg';
import { ContentState, EditorState, convertFromHTML} from 'draft-js';
import {convertHTMlToRaw} from "../../utils/HTMLDraft";
import SelectTopics from "../../components-user/topics/SelectTopics";
import { generateClient } from 'aws-amplify/api';
import { useNavigate, useParams } from "react-router-dom";
import { captureError } from "../../utils/sentryHelpers";
import { uploadPublicDocument, validatePublicDocument } from "../../utils/uploadPublicDocument";
import { useIndustry } from "../../utils/industryContextHook";
import { getLocalizedText } from "../../utils/localizedText";
import { UserContext } from "../../App";
import SlugField from "../../components/toolbox/SlugField";
import { isAdminOrModerator } from "../../utils/roles";
import { slugMutationInput } from "../../utils/slugInput";
import { pickSourceText } from "../../utils/slugify";


const client = generateClient();

const CreateNewsPage = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    const { companyId, newsId } = useParams();

    const { t, i18n } = useTranslation();
    const { currentIndustry } = useIndustry();
    const { user } = useContext(UserContext);
    // Public URL slug: Admin/Moderator only (AppSync field auth rejects other inputs containing "slug")
    const canEditSlug = isAdminOrModerator(user);
    // Slug as loaded with the news; "slug" is only sent when it was changed here (see slugMutationInput)
    const [loadedSlug, setLoadedSlug] = useState(null);
    const [newsData, setNewsData] = useState({
        name: [],
        content: [],
        image: {},
        file: {},
        cMSNewsEmployeeId: "",
        cMSNewsTopicId: "",
        conditions: [],
        isHero: false,
        isHero2: false,
    });

    const getNewsData = async () => {
      try {
        const newsData = await client.graphql({
          variables: { id: newsId },
          query: /* GraphQL */ `
          query GetCMSNews($id: ID!) {
            getCMSNews(id: $id) {
              id
              type
              company {
                id
                name
                status
                createdAt
                companyAdminId
                image {
                    main {
                        fileName
                        alt
                    }
                    }
                adminEmployees {
                    items {
                      status
                      image {
                        fileName
                        alt
                      }
                      user {
                        lastName
                        firstName
                        title
                        contact {
                          email
                          phone
                        }
                      }
                    }
                  }
              }
              employee {
                id
                contact {
                  email
                  phone
                  mobile
                  fax
                  address {
                    street
                    streetNumber
                    company
                    zip
                    city
                    countryCode
                  }
                }
                user {
                  id
                  email
                  firstName
                  middleName
                  lastName
                  salutation
                  title
                  status
                  createdAt

                } 
              }
              topic {
                id
                name {
                  language
                  text
                }
                color
                topicGroup {
                  id
                  color
                }
                topicGroupTopicsId
              }
              name {
                language
                text
              }
              slug
              slugHistory
              image {
                fileName
                alt
              }
              excerpt {
                language
                text
              }
              interests {
                name {
                  language
                  text
                }
                id
              }
              content {
                language
                text
              }
              file {
                url
                s3Path
                fileName
                type
                name
              }
              conditions {
                name
                accepted
              }
              status
              approved
              adminComment
              isHero
              isHero2
              createdAt
              updatedAt
              companyNewsId
              employeeNewsId
              cMSNewsEmployeeId
              cMSNewsTopicId
              owner
              __typename
            }
          }
        `,
        });
        console.log(newsData);
       
        return {
          ...newsData.data.getCMSNews,
          topics: newsData.data.getCMSNews.topics?.items?.map(topic => topic.topicId) || []};
      } catch (error) {
        console.warn("Error getting news data", error);
        captureError(error, {
          feature: 'news-admin',
          operation: 'get-news-data',
          context: { newsId },
          tags: { component: 'EditNews' }
        });
      }
    };

  const intialLoadNews = async () => {
    console.log("loading news");
    setIsLoading(true);
    const dbNews = await getNewsData();
    setNewsData(dbNews);
    setLoadedSlug(dbNews?.slug ?? null);
    if(dbNews.content) {
      const deContent = dbNews.content.find(c => c.language === "de")?.text;
      const enContent = dbNews.content.find(c => c.language === "en")?.text;
      setEditorState({
        de: deContent ? EditorState.createWithContent(
                        ContentState.createFromBlockArray(
                          convertFromHTML(deContent)
                        )
                      ) : EditorState.createEmpty(),
        en: enContent ? EditorState.createWithContent(
                        ContentState.createFromBlockArray(
                          convertFromHTML(enContent)
                        )
                      ) : EditorState.createEmpty(),
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (!companyId) {
      console.warn("No company id provided");
      return;
    } else if (!newsId) {
      console.log("No lecture id provided");
      return;
    } else {
      intialLoadNews();
    }
  }, []);

 

  const [validationErrors, setValidationErrors] = useState([]);

  
  const validateNews = () => {
    const errors = [];
    if (newsData.name.length !== 2) {
      errors.push(t("Please enter a title in both languages"));
    }
    // if(newsData.content.find(t => t.language === "de")?.text?.length < 1000 || newsData.content.find(t => t.language === "en")?.text?.length < 1000) {
    //   errors.push(t("The content must not be longer than 1000 characters"));
    // }
    if(!newsData.cMSNewsTopicId) {
      errors.push(t("Please select a topic"));
    }
    if(newsData.content.length !== 2) {
      errors.push(t("Please enter content in both languages"));
    }
    if(!newsData.cMSNewsEmployeeId) {
      errors.push(t("Please select an employee"));
    }
    if(!newsData.image?.fileName) {
      errors.push(t("Please upload an image"));
    }
    if(newsData.image?.fileName && !newsData.conditions?.find(c => c.name === "image")?.accepted) {
      errors.push(t("Please agree to the image conditions"));
    }
    if(newsData.file?.fileName && !newsData.conditions?.find(c => c.name === "file")?.accepted) {
      errors.push(t("Please agree to the file conditions"));
    }

    setValidationErrors(errors);
    return errors.length === 0;
  }

  // Helper function to find and deactivate hero news with pagination
  const findAndDeactivateHeroNews = async (heroField) => {
    try {
      let allHeroNews = [];
      let nextToken = null;

      // Paginate through all news to find hero news
      do {
        const result = await client.graphql({
          query: /* GraphQL */ `
            query ListCMSNews(
              $filter: ModelCMSNewsFilterInput
              $limit: Int
              $nextToken: String
            ) {
              listCMSNews(filter: $filter, limit: $limit, nextToken: $nextToken) {
                items {
                  id
                  ${heroField}
                }
                nextToken
              }
            }
          `,
          variables: {
            filter: { [heroField]: { eq: true } },
            nextToken: nextToken
          }
        });

        allHeroNews.push(...result.data.listCMSNews.items);
        nextToken = result.data.listCMSNews.nextToken;
      } while (nextToken);

      // Deactivate all found hero news
      for (const news of allHeroNews) {
        if (news.id !== newsId) { // Don't deactivate the current news item
          await client.graphql({
            query: /* GraphQL */ `
              mutation UpdateCMSNews($input: UpdateCMSNewsInput!) {
                updateCMSNews(input: $input) {
                  id
                }
              }
            `,
            variables: {
              input: {
                id: news.id,
                [heroField]: false
              }
            }
          });
        }
      }

      return allHeroNews.length;
    } catch (error) {
      console.error("Error deactivating hero news:", error);
      captureError(error, {
        feature: 'news-admin',
        operation: 'deactivate-hero-news',
        context: { heroField, newsId },
        tags: { component: 'EditNews' }
      });
      throw error;
    }
  }

  // Handler for hero news toggle
  const [isUpdatingHero, setIsUpdatingHero] = useState(false);

  const handleHeroToggle = async (heroField, newValue) => {
    setIsUpdatingHero(true);
    try {
      // Determine the other hero field
      const otherHeroField = heroField === 'isHero' ? 'isHero2' : 'isHero';

      if (newValue) {
        // Activating hero news - need to deactivate others first
        await findAndDeactivateHeroNews(heroField);
      }

      // Update the current news in database
      // If activating one hero news, deactivate the other
      const updateInput = {
        id: newsId,
        [heroField]: newValue
      };

      if (newValue) {
        // When activating one, deactivate the other
        updateInput[otherHeroField] = false;
      }

      await client.graphql({
        query: /* GraphQL */ `
          mutation UpdateCMSNews($input: UpdateCMSNewsInput!) {
            updateCMSNews(input: $input) {
              id
              isHero
              isHero2
            }
          }
        `,
        variables: {
          input: updateInput
        }
      });

      // Update local state
      setNewsData(oldNewsData => ({
        ...oldNewsData,
        [heroField]: newValue,
        ...(newValue ? { [otherHeroField]: false } : {})
      }));
      console.log(`Successfully updated ${heroField} to ${newValue}`);
    } catch (error) {
      console.error("Failed to update hero news:", error);
      captureError(error, {
        feature: 'news-admin',
        operation: 'toggle-hero-news',
        context: { heroField, newValue, newsId },
        tags: { component: 'EditNews' }
      });
    } finally {
      setIsUpdatingHero(false);
    }
  }

  const createNews = async () => {
    try {
      if(!validateNews()) {
        return;
      }
      setIsLoading(true);
      const response = await client.graphql({
        query: newsId ? /* GraphQL */ `
        mutation UpdateCMSNews($input: UpdateCMSNewsInput!) {
          updateCMSNews(input: $input) {
            id  
          }
        }
        ` : /* GraphQL */ `
        mutation CreateCMSNews(
          $input: CreateCMSNewsInput!
          $condition: ModelCMSNewsConditionInput
        ) {
          createCMSNews(input: $input, condition: $condition) {
              id
            }
          }
        `,
        variables: {
          input: {
            companyNewsId: companyId,
            name: newsData.name,
            content: newsData.content,
            image: newsData.image,
            file: newsData.file,
            cMSNewsEmployeeId: newsData.cMSNewsEmployeeId,
            cMSNewsTopicId: newsData.cMSNewsTopicId,
            conditions: newsData.conditions,
            ...newsId ? {id: newsId} : {},
            adminComment: newsData.adminComment,
            status: newsData.status,
            approved: newsData.approved,
            isHero: newsData.isHero,
            isHero2: newsData.isHero2,
            // "slug" only for Admin/Moderator and only when changed vs. the loaded slug; slugHistory is never sent
            ...slugMutationInput(newsData.slug, { canEditSlug, isUpdate: !!newsId, loadedSlug })
          },
        },
      });
      console.log("response", response);
      setTimeout(() => {
        intialLoadNews();
        setIsLoading(false);
      }, 2000);
    } catch (error) {
      setIsLoading(false);
      console.log("error", error);
      captureError(error, {
        feature: 'news-admin',
        operation: 'update-news',
        context: { newsId, companyId, newsData },
        tags: { component: 'EditNews' }
      });
    }
  };

    // Topics
  // Select Topics
  const [topics, setTopics] = useState([]);
  const [topicGroups, setTopicGroups] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);

  useEffect(() => {
    if (newsData.cMSNewsTopicId) {
      setSelectedTopics([newsData.cMSNewsTopicId]);
    }
  }, [newsData.cMSNewsTopicId]);

  const [selectTopicsModal, setSelectTopicsModal] = useState(false);
  const toggleSelectTopicsModal = () => setSelectTopicsModal(!selectTopicsModal);

  const handleSetSelectedTopics = (topics) => {
    setSelectedTopics(topics);
    setNewsData(oldNewsData => ({...oldNewsData, cMSNewsTopicId: topics[0]}));
    // setLectureData(oldLecture => ({...oldLecture, topics}));
  }

  // Topics data

  const getTopics = async () => {
      const filter = currentIndustry?.id ? { industryId: { eq: currentIndustry.id } } : undefined;
      try {
        const topicGroupsWithTopics = await client.graphql({
          query: /* GraphQL */ `
          query ListTopicGroups(
            $filter: ModelTopicGroupFilterInput
            $limit: Int
            $nextToken: String
          ) {
            listTopicGroups(filter: $filter, limit: $limit, nextToken: $nextToken) {
              items {
                id
                color
                name {
                  language
                  text
                }
                topics {
                  items {
                    id
                    name {
                      language
                      text
                    }
                    topicGroupTopicsId
                  }
                }
              }
              nextToken
              __typename
            }
          }
        `,
        variables: {
          filter,
          limit: 1000
        }
          });
          setTopicGroups(topicGroupsWithTopics.data.listTopicGroups.items);
          const topics = topicGroupsWithTopics.data.listTopicGroups.items.map((topicGroup) => topicGroup.topics.items).flat();
          setTopics(topics);

      } catch (error) {
          console.log("error on fetching topics", error);
          captureError(error, {
            feature: 'news-admin',
            operation: 'fetch-topics',
            tags: { component: 'EditNews' }
          });
      }
  }

  useEffect(() => {
      if (currentIndustry?.id) {
          getTopics();
      }
  }, [currentIndustry?.id]);

  const [unknownTopicNames, setUnknownTopicNames] = useState({});

  useEffect(() => {
      const unknownIds = selectedTopics.filter(id => topics.length > 0 && !topics.find(t => t.id === id));
      if (unknownIds.length === 0) return;
      const fetchUnknownTopics = async () => {
          const names = {};
          for (const id of unknownIds) {
              try {
                  const res = await client.graphql({
                      query: /* GraphQL */ `query GetTopic($id: ID!) { getTopic(id: $id) { id name { language text } } }`,
                      variables: { id }
                  });
                  if (res.data.getTopic?.name) names[id] = res.data.getTopic.name;
              } catch (e) { /* topic may have been deleted */ }
          }
          setUnknownTopicNames(names);
      };
      fetchUnknownTopics();
  }, [selectedTopics, topics]);

  const Topics = ({topics, topicGroups, selectedTopics}) => {

    // First sort the topics by topicGroup (new array with objects for eacht group, inside object the key: topics, inside the topics for this group), then return for each group a col, inside the col the title of the group, then the topics as chips
    if(topics?.length === 0 || topicGroups?.length === 0 || selectedTopics?.length === 0) {
        return null;
    }
    let sortedTopics = [];
    let unknownTopicIds = [];
    selectedTopics.forEach((selectedTopic) => {
        const topicData = topics.find((topic) => topic.id === selectedTopic);
        if (!topicData) {
            unknownTopicIds.push(selectedTopic);
            return;
        }
        const topicGroup = topicGroups.find((topicGroup) => topicGroup.id === topicData.topicGroupTopicsId);
        const topicGroupIndex = sortedTopics.findIndex((sortedTopic) => sortedTopic.id === topicGroup.id);
        if (topicGroupIndex === -1) {
            sortedTopics.push({...topicGroup, topics: [topicData]});
        } else {
            sortedTopics[topicGroupIndex].topics.push(topicData);
        }
    });
    return (
        <MDBRow className="my-2">
            {unknownTopicIds.length > 0 && (
                <MDBCol size="12" className="mb-3">
                    <div className="d-flex flex-wrap gap-1">
                        {unknownTopicIds.map((id) => (
                            <MDBChip key={id} className="m-0 d-flex align-items-center" bgColor="warning" textColor="dark">
                                <MDBIcon fas icon="exclamation-triangle" className="me-1" />
                                {unknownTopicNames[id] ? getLocalizedText(unknownTopicNames[id]) : t("Unknown topic")} — {t("not available in this industry")}
                                <MDBIcon fas icon="times" className="ms-2 cursor-pointer" onClick={() => handleSetSelectedTopics(selectedTopics.filter(sid => sid !== id))} />
                            </MDBChip>
                        ))}
                    </div>
                </MDBCol>
            )}
            {sortedTopics.map((topicGroup) => {
                return (
                    <MDBCol size="12" xxl="6" key={topicGroup.id} className="mb-3 mb-lg-4">
                        <h6>{topicGroup.name?.find(n => n.language === i18n.language)?.text || topicGroup.name?.[0]?.text}</h6>
                        <MDBRow>
                            {topicGroup.topics.map((topic) => {
                                return (
                                    <MDBCol size="12" key={topic.id}>
                                        <MDBChip className="m-1 float-start" bgColor={topicGroup.color} textColor="white">{topic?.name?.find((name) => name.language === i18n.language)?.text || topic.name[0]?.text}</MDBChip>
                                    </MDBCol>
                                )
                            })}
                        </MDBRow>
                    </MDBCol>
                )
            })}
        </MDBRow>
    )
}

// Select Employee
const [employee, setEmployee] = useState();
const [selectEmployeeModal, setSelectEmployeeModal] = useState(false);
const toggleSelectEmployeeModal = () => setSelectEmployeeModal(!selectEmployeeModal);

const getEmployee = async () => {
    try {
      const employee = await client.graphql({
        query: /* GraphQL */ `
        query GetEmployee($id: ID!) {
            getEmployee(id: $id) {
              id
              user {
                id
                firstName
                lastName
                title
              }
              image {
                fileName
                alt
              }
            }
          }
        `,
        variables: { id: newsData.cMSNewsEmployeeId },
      });
      setEmployee(employee.data.getEmployee);
    } catch (error) {
      console.log("error on fetching employee", error);
      captureError(error, {
        feature: 'news-admin',
        operation: 'fetch-employee',
        context: { employeeId: newsData.cMSNewsEmployeeId },
        tags: { component: 'EditNews' }
      });
    }
  };


useEffect(() => {
    if (newsData.cMSNewsEmployeeId) {
        getEmployee();
    }
}, [newsData.cMSNewsEmployeeId]);

// Editor
// Editor
const [editorState, setEditorState] = useState({
    de: EditorState.createEmpty(),
    en: EditorState.createEmpty(),
})
const onEditorStateChange = (editorState, language ) => {
  setEditorState(oldState => ({...oldState, [language]: editorState}));
  setNewsData(oldNewsData => ({...oldNewsData, content: [...oldNewsData.content.filter(c => c.language !== language), {language, text: convertHTMlToRaw(editorState)}] }));

} 

// Image and Script

const [isUploadingFile, setIsUploadingFile] = useState(false);
const [uploadingFileError, setUploadingFileError] = useState(null);

const handleSetFile = async (file) => {
  if (!file) return;
  const validationError = validatePublicDocument(file, t);
  if (validationError) {
    setUploadingFileError(validationError);
    return;
  }

  setIsUploadingFile(true);
  setUploadingFileError(null);
  try {
    const uploadedFile = await uploadPublicDocument(file, { companyId, fileType: "newsFile" });
    setNewsData(oldNewsData => ({ ...oldNewsData, file: uploadedFile }));
  } catch (error) {
    captureError(error, {
      feature: 'news',
      operation: 'upload-file-s3',
      context: { companyId, newsId, fileName: file.name, fileSize: file.size },
      tags: { component: 'AdminEditNews' }
    });
    setUploadingFileError(error?.errors?.[0]?.message || t("Upload failed. Please try again."));
  } finally {
    setIsUploadingFile(false);
  }
}

const [isUploadingLectureImage, setIsUploadingLectureImage] = useState(false);
const [uploadingLectureImageError, setUploadingLectureImageError] = useState(null);

const handleSetImage = (file) => {
  // upload file using mutation: uploadLectureFile(base64FileBuffer: String, companyId: String): LambdaS3Response @function(name: "uploadLectureFiles-${env}")
  if(file) {
    try {
      setIsUploadingLectureImage(true);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onerror = error => {
      console.log('Error reader: ', error);
      setUploadingLectureImageError(error);
    }
    reader.onload = async () => {
      try {
        console.log(reader.result)
        const base64FileBuffer = reader.result.replace(/^data:.+;base64,/, '');
        const response = await client.graphql({
          query: /* GraphQL */ `
          mutation UploadCompanyImage($imageBuffer: String, $imageType: String) {
            uploadCompanyImage(imageBuffer: $imageBuffer, imageType: $imageType)
          }
        `,
          variables: {
            imageBuffer: base64FileBuffer,
            imageType: "newsImage",
          },
        });
        console.log("response", response);
        const fileName = response.data.uploadCompanyImage.replace(/{|}|fileName=/g, '');
        setNewsData(oldNewsData => ({...oldNewsData, image: {fileName, alt: "Lecture Image"}}));
        
        } catch (error) {
          console.log("error", error);
          setIsUploadingLectureImage(false);
          setUploadingLectureImageError(error.errors[0].message);
        }
    }
    setIsUploadingLectureImage(false);
    setUploadingLectureImageError(null);
    } catch (error) {
      console.log("error", error);
      setIsUploadingLectureImage(false);
      setUploadingLectureImageError(error.errors[0].message);
    }
  }
}


return (
<>

    <MDBCard>
      <MDBCardBody>
        <MDBRow>
          <MDBCol size="12" lg="6" xl="4">
            <MDBTypography tag="h5">{t("Event")}</MDBTypography>
            <MDBTypography tag="h6">Lounges 2024</MDBTypography>
            <br></br>
            <MDBTypography tag="h5">{t("Company")}</MDBTypography>
            <>
              {newsData.company?.image?.main?.fileName ?
                  <div className='d-flex align-items-center'>
                      {newsData.company?.image?.main?.fileName ?
                          <img
                              src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/"+newsData.company.image.main.fileName}
                              className="rounded-circle border"
                              style={{ height: "50px", width: "50px", objectFit: "contain" }}
                              alt={newsData.company.image.alt}
                          />
                      :
                          <MDBIcon className="rounded-circle border" style={{ height: "50px", width: "50px", textAlign: "center", paddingTop: "12px", fontSize: "20px" }} fas icon="company-alt" />
                                  
                      }
                      <div className='ms-3'>
                      <p className='fw-bold mb-1'>{newsData.company?.name}</p>
                      {/* <p className='text-muted mb-0'><a href={company.website} target="_blank" rel="noreferrer">{company.website}</a></p> */}
                      </div>
                  </div>
                  :
                  <div className='d-flex align-items-center'>
                    <MDBIcon
                        className="rounded-circle border"
                        style={{
                            height: "50px",
                            width: "50px",
                            textAlign: "center",
                            paddingTop: "12px",
                            fontSize: "20px",
                        }}
                        fas
                        icon="user-alt"
                    />
                    <div className='ms-3'>
                      <p className='fw-bold mb-1'>{newsData.company?.name}</p>
                      <p className='text-muted mb-0'><a href={newsData.company?.website} target="_blank" rel="noreferrer">{newsData.company?.website}</a></p>
                    </div>
                  </div>
              }
            </>
            <MDBTypography tag="h5" className="mt-2 mt-xl-3">{t("Company Admins")}</MDBTypography>
            {newsData.company?.adminEmployees?.items?.length > 0 && newsData.company?.adminEmployees?.items.map((admin, index) => (
              <div className='d-flex align-items-center'>
              {admin.image?.fileName ?
                          <img
                              src={"https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/"+ admin.image.fileName}
                              className="rounded-circle border"
                              style={{ height: "50px", width: "50px", objectFit: "contain" }}
                              alt={admin.image.alt}
                          />
                          :
                          <MDBIcon className="rounded-circle border" style={{ height: "50px", width: "50px", textAlign: "center", paddingTop: "12px", fontSize: "20px" }} fas icon="user-alt" />
                          
              }
                <div className='ms-3'>
                <p className='fw-bold mb-1'>{admin.user.title ? admin.user.title + " " : ""}{admin.user.firstName} {admin.user.middleName} {admin.user.lastName}{admin.status === "pending" && <MDBChip className="bg-warning text-white bg-opacity-75 inline" style={{maxWidth: "fit-content"}}>{t("Pending")}</MDBChip>}</p>
                <p className='text-muted mb-0'>{admin.user.contact?.email}</p>
                <p className='text-muted mb-0'>{admin.user.contact?.phone}</p>
                </div>
              </div>
            ))}
            
          </MDBCol>
          <MDBCol size="12" lg="6" xl="4">
            <MDBTypography tag="h5">{t("Erstellung & Update")}</MDBTypography>
            <MDBTypography tag="h6">{t("Erstellt")}: {new Date(newsData?.createdAt).toLocaleString("de-de")}</MDBTypography>
            <MDBTypography tag="h6">{t("Zuletzt aktualisiert")}: {new Date(newsData?.updatedAt).toLocaleString("de-de")}</MDBTypography>
            <MDBTypography tag="h5">{t("Comment")}</MDBTypography>
            <MDBTextArea datapath="adminComment" value={newsData?.adminComment || ""} onChange={e => setNewsData(old => ({...old, adminComment: e.target.value}))} />
          </MDBCol>
          <MDBCol size="12" lg="6" xl="4">
          
         
            {/* Status */}
            <div className='d-flex justify-content-between mt-2'>
              <MDBTypography tag="h6">{t("Status")}</MDBTypography>
              <MDBSelect
                size="sm"
                data={[
                  { text: t("Created"), value: "created" },
                  { text: t("Approved"), value: "checked" },
                  { text: t("Not Approved"), value: "not_approved" }
                ]}
                datapath="status"
                value={newsData?.status}
                onChange={e => setNewsData(old => ({...old, status: e.value}))}
              />
            </div>
            {/* Approved */}
            <div className='d-flex justify-content-between mt-2'>
              <MDBTypography tag="h6">{t("Approved")}</MDBTypography>
              <div>

                  <MDBCheckbox
                    name='approved'
                    id='approved_yes'
                    label={t("Yes")}
                    inline
                    checked={newsData?.approved === true}
                    onChange={(e) => setNewsData(oldNewsData => ({...oldNewsData, approved: true}))}
                  />

                  <MDBCheckbox
                    name='approved'
                    id='approved_no'
                    label={t("No")}
                    inline
                    checked={!newsData?.approved}
                    onChange={(e) => setNewsData(oldNewsData => ({...oldNewsData, approved: false}))}
                  />
              </div>
            </div>
            {/* Hero News 1 */}
            <div className='d-flex justify-content-between mt-3'>
              <MDBTypography tag="h6">{t("Hero News 1")}</MDBTypography>
              <MDBSwitch
                id='isHero'
                checked={newsData?.isHero || false}
                onChange={(e) => handleHeroToggle('isHero', e.target.checked)}
                disabled={isUpdatingHero}
              />
            </div>
            {/* Hero News 2 */}
            <div className='d-flex justify-content-between mt-2'>
              <MDBTypography tag="h6">{t("Hero News 2")}</MDBTypography>
              <MDBSwitch
                id='isHero2'
                checked={newsData?.isHero2 || false}
                onChange={(e) => handleHeroToggle('isHero2', e.target.checked)}
                disabled={isUpdatingHero}
              />
            </div>
          </MDBCol>
        </MDBRow>
        {
          isLoading ? (
            <MDBSpinner color="primary" className="float-end mt-3" />
          )
          :
          <div className="float-end mt-3">
          <MDBBtn color="primary" onClick={() => createNews()}>{t("Save")}</MDBBtn>
          <br></br>
          <MDBBtn color="link" onClick={() => navigate(-1)}>{t("Back")}</MDBBtn>
          </div>
          
          
        }
        <br></br>
        
      </MDBCardBody>
    </MDBCard>
<MDBRow className=" mt-5 h-auto d-flex justify-content-center">
<MDBCol size="12" xl="12" xxl="11">
<MDBCard>
<MDBCardBody className="px-1 px-md-5">

{/* Topics */}
<MDBRow className="d-flex justify-content-evenly mt-2 mt-xl-4">
    <MDBCol size="12" lg="10" xl="8" xxl="7">
        <div className="mb-2 mb-lg-4">
            <MDBTypography tag="h5">
            {t("Topic")}
            </MDBTypography>
           
        </div>
        <div className="w-100 text-center">
        <MDBBtn onClick={() => toggleSelectTopicsModal()} color="primary" outline>
            {t("Select Topic")}
        </MDBBtn>
        <MDBRow className="w-100">
            <MDBCol>
            {topics.length > 0 && topicGroups.length > 0 && newsData.cMSNewsTopicId && (
                <Topics
                topics={topics}
                topicGroups={topicGroups}
                selectedTopics={[newsData.cMSNewsTopicId] || []}
                setSelectedTopics={handleSetSelectedTopics}
                />
            )}
            </MDBCol>
        </MDBRow>
      </div>
    </MDBCol>
</MDBRow>
{/* Topics END */}

{/* TITEL */}
<MDBRow className="d-flex justify-content-evenly mt-2 mt-xl-4">
    <MDBCol size="12" lg="10" xl="8" xxl="7">
    <hr className="mb-1 mb-xl-2" />
        <div className="mb-2 mb-lg-4">
            <MDBTypography tag="h5">
                {t("Title")}
            </MDBTypography>
        </div>
        <MDBInputGroup className="mb-1" textAfter={<span style={newsData.name?.find((t) => t.language === "de")?.text?.length === 60 ? {color: "red", fontWeight: "bold"} : {}}>{((newsData?.name?.length > 0? newsData.name.find((t) => t.language === "de")?.text: "")?.length || "0") + "/60"}</span>}>
            <div className="input-group-text">
            <ReactCountryFlag
                countryCode="de"
                svg
                style={{
                width: "20px",
                height: "20px",
                }}
                title={i18n.language}
            /> 
            </div>
            <MDBInput
            label={t("Title German")}
            type="text"
            maxLength="60"
            value={newsData?.name?.find((t) => t.language === "de")?.text || ""}
            onChange={(e) => setNewsData({...newsData, name: [{language: "de", text: e.target.value}, ...newsData.name.filter((t) => t.language !== "de")]})}
            />
        </MDBInputGroup>
        <MDBInputGroup className="mb-1" textAfter={<span style={newsData.name?.find((t) => t.language === "en")?.text?.length === 60 ? {color: "red", fontWeight: "bold"} : {}}>{((newsData?.name?.length > 0? newsData.name.find((t) => t.language === "en")?.text: "")?.length || "0") + "/60"}</span>}>
            <div className="input-group-text">
            <ReactCountryFlag
                countryCode="gb"
                svg
                style={{
                width: "20px",
                height: "20px",
                }}
                title={i18n.language}
            />
            </div>
            <input
            className="form-control"
            placeholder={t("Title English")}
            type="text"
            maxLength="60"
            value={newsData?.name?.find((t) => t.language === "en")?.text || ""}
            onChange={(e) => setNewsData({...newsData, name: [{language: "en", text: e.target.value}, ...newsData.name.filter((t) => t.language !== "en")]})}
            
            />
        </MDBInputGroup>
        {/* Public URL slug (Admin/Moderator only) */}
        {canEditSlug && (
            <div className="mt-3">
                <SlugField
                    entity="news"
                    entityId={newsId}
                    value={newsData?.slug}
                    history={newsData?.slugHistory}
                    sourceText={pickSourceText(newsData?.name)}
                    onChange={(slug) => setNewsData(oldNewsData => ({...oldNewsData, slug}))}
                />
            </div>
        )}
    </MDBCol>
</MDBRow>
{/* TITEL END */}

{/* LECTURE DESCRIPTION */}
<MDBRow className="d-flex justify-content-evenly mt-2 mt-xl-4">
    <MDBCol size="12" lg="10" xl="8" xxl="7">
    <hr className="my-4" />
    <div className="mb-2 mb-lg-4">
        <MDBTypography tag="h5">
        {t("Content")}
        </MDBTypography>
    </div>
    <ReactCountryFlag
        countryCode="de"
        svg
        style={{
        width: "20px",
        height: "20px",
        }}
        title={i18n.language}
    /> 
    <Editor
        editorState={editorState["de"]}
        editorClassName="md-textarea form-control"
        onEditorStateChange={e => onEditorStateChange(e, "de")}
        toolbar={{
          options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
        }}
      />
      <br></br>
      <ReactCountryFlag
        countryCode="gb"
        svg
        style={{
        width: "20px",
        height: "20px",
        }}
        title={i18n.language}
    />
    <Editor
        editorState={editorState["en"]}
        editorClassName="md-textarea form-control"
        onEditorStateChange={e => onEditorStateChange(e, "en")}
        toolbar={{
          options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
        }}
      />
    </MDBCol>
</MDBRow>
{/* LECTURE DESCRIPTION END */}

{/* LECTURE IMAGE */}
<MDBRow className="d-flex justify-content-evenly">
  <MDBCol size="12" lg="10" xl="8" xxl="7">
  <hr className="my-4" />
    <div className="mb-2 mb-lg-4">
      <MDBTypography tag="h5">
      {t("Image")}
      </MDBTypography>

    </div>
    <div className="w-100 text-center mb-2 mb-lg-4">
    <img
      src={newsData?.image?.fileName ? 
      "https://iec-cp-public.s3.eu-central-1.amazonaws.com/newsImages/" + newsData?.image.fileName: "https://placehold.co/600x400"}
      className="img-thumbnail"
      style={{ height: "200px", width: "400px", objectFit: "cover" }}
      alt='...' />
    </div>
    <MDBFile id='fileDE' onChange={e => handleSetImage(e.target.files[0])} />
    
    
   
    <div className="mt-3">
      <MDBCheckbox
        name="image-condition"
        id="image-condition"
        label={t("I agree that the image will be made public and we have the rights to use the image.")}
        checked={newsData?.conditions?.findIndex(c => c.name === "image") > -1 ? newsData?.conditions[newsData?.conditions?.findIndex(c => c.name === "image")].accepted : false}
        onChange={(e) => {setNewsData(oldNewsData => ({...oldNewsData, conditions: [{name: "image", accepted: e.target.checked}, ...oldNewsData.conditions?.filter(c => c.name !== "image")]}))}}
      />
    </div>
  </MDBCol>
 
</MDBRow>
{/* LECTURE IMAGE END */}

{/* LECTURE SCRIPT */}
<MDBRow className="d-flex justify-content-evenly">
  <MDBCol size="12" lg="10" xl="8" xxl="7">
  <hr className="my-4" />
    <div className="mb-2 mb-lg-4">
      <MDBTypography tag="h5">
      {t("File")}
      </MDBTypography>
    </div>
    {newsData?.file?.url && (
      <p className="text-muted">
        {t("File uploaded successfully")}{' '}
        <a href={newsData.file.url} target="_blank" rel="noopener noreferrer">
          <MDBIcon fas icon="file-pdf" className="me-1" />
          {newsData.file.name || newsData.file.fileName}
        </a>
      </p>
    )
    }
    {isUploadingFile ? (
      <div className="text-center py-2">
        <MDBSpinner size="sm" role="status" />
      </div>
    ) : (
      <MDBFile id='newsFile' accept="application/pdf" onChange={e => handleSetFile(e.target.files[0])} />
    )}
    {uploadingFileError && (
      <div className="text-danger small mt-1">{uploadingFileError}</div>
    )}

    <div className="mt-3">
      <MDBCheckbox
        name="script-condition"
        id="script-condition"
        label={t("I confirm that I have the rights to use the file and that it can be made public.")}
        checked={newsData?.conditions?.findIndex(c => c.name === "file") > -1 ? newsData?.conditions[newsData?.conditions?.findIndex(c => c.name === "file")].accepted : false}
        onChange={(e) => {setNewsData(oldNewsData => ({...oldNewsData, conditions: [{name: "file", accepted: e.target.checked}, ...oldNewsData.conditions?.filter(c => c.name !== "file")]}))}}
      />
    </div>
    
  </MDBCol>
  
</MDBRow>
{/* LECTURE SCRIPT END */}

{/* Employee */}
<MDBRow className="d-flex justify-content-evenly mt-2 mt-xl-4">
    <MDBCol size="12" lg="10" xl="8" xxl="7">
    <hr className="my-4" />
        <div className="mb-2 mb-lg-4">
            <MDBTypography tag="h5">
            {t("Contact person")}
            </MDBTypography>
        </div>
        <div className="w-100 text-center">
        <MDBBtn onClick={() => toggleSelectEmployeeModal()} color="primary" outline>
            {t("Select employee")}
        </MDBBtn>
        {employee && (
          <div className="d-flex gap-4 align-items-center">
                  {employee.image?.fileName ? (
                    <img
                      src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/${employee.image.fileName}`}
                      className="rounded-circle border"
                      style={{
                        height: "50px",
                        width: "50px",
                        objectFit: "cover",
                      }}
                      alt={employee.image.alt}
                    />
                  ) : (
                    <MDBIcon
                      className="rounded-circle border"
                      style={{
                        height: "50px",
                        width: "50px",
                        textAlign: "center",
                        paddingTop: "12px",
                        fontSize: "20px",
                      }}
                      fas
                      icon="user-alt"
                    />
                  )}
                  <div>
                    <h6>{`${employee.user?.title ? employee.user?.title + " ": ""}${employee.user?.firstName} ${employee.user?.middleName || ""} ${employee.user?.lastName}`}</h6>
                    <span>{employee.department?.items?.[0]?.department.name[0].text}</span>
                  </div>
                </div>
        )}
      </div>
    </MDBCol>
</MDBRow>
{/* Employee END */}
<hr className="my-4" />
<MDBRow className="d-flex justify-content-evenly">
<MDBCol size="12" lg="10" xl="8" xxl="7">
{validationErrors.length > 0 && (
  <div className="alert alert-danger" role="alert">
    {validationErrors.map((error) => (
      <p key={error}>{error}</p>
    ))}
  </div>
)}


</MDBCol>
</MDBRow>

</MDBCardBody>
</MDBCard>
</MDBCol>
</MDBRow>

<MDBModal open={selectTopicsModal} onClose={() => setSelectTopicsModal(false)} staticBackdrop>
  <MDBModalDialog centered size="xl">
      <MDBModalContent >
          <MDBModalHeader>
              <MDBModalTitle>Select Topics</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={toggleSelectTopicsModal}></MDBBtn>
          </MDBModalHeader>
          <MDBModalBody>
              <SelectTopics
                isActive={selectTopicsModal}
                toggleActive={toggleSelectTopicsModal}
                selectedTopics={selectedTopics}
                setSelectedTopics={handleSetSelectedTopics}
                parentTopics={topics}
                parentTopicGroups={topicGroups}
                maxSelect={1}
                />
          </MDBModalBody>
          <MDBModalFooter>
              <MDBBtn color="secondary" onClick={toggleSelectTopicsModal}>{t("Close")}</MDBBtn>
          </MDBModalFooter>
      </MDBModalContent>
  </MDBModalDialog>
</MDBModal>
<MDBModal open={selectEmployeeModal} onClose={() => setSelectEmployeeModal(false)} staticBackdrop>
  <MDBModalDialog centered size="xl">
      <MDBModalContent >
          <MDBModalHeader>
              <MDBModalTitle>{t("Select employee")}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={toggleSelectEmployeeModal}></MDBBtn>
          </MDBModalHeader>
          <MDBModalBody>
              <EmployeeSelector
              companyId={companyId}
              selectedEmployees={newsData.cMSNewsEmployeeId ? [newsData.cMSNewsEmployeeId]: []}
              setSelectedEmployees={(employees) => setNewsData({...newsData, cMSNewsEmployeeId: employees[0]})}
              maxSelect={1}
              />
          </MDBModalBody>
          <MDBModalFooter>
              <MDBBtn color="secondary" onClick={toggleSelectEmployeeModal}>{t("Close")}</MDBBtn>
          </MDBModalFooter>
      </MDBModalContent>
  </MDBModalDialog>
</MDBModal>

</>
);
};

export default CreateNewsPage;
